/**
 * Whether this training session id has already been used and ended.
 *
 * The admin portal mints one session id per launch; reopening an old link
 * would otherwise start a second session under an id whose results exist.
 * A failed check does not block: better to allow a session than to refuse
 * one on a network blip.
 */
import { useEffect, useState } from 'react';

export function useTrainingSessionEnded(sessionId: string | undefined, isEnabled: boolean): boolean {
  const [hasEnded, setHasEnded] = useState(false);

  useEffect(() => {
    if (!sessionId || !isEnabled) return;
    let isCancelled = false;

    const checkSession = async (): Promise<void> => {
      try {
        const { apiService } = await import('../services/apiService');
        const response = await apiService.get(`/training/sessions/check/${sessionId}?_t=${Date.now()}`);
        if (!isCancelled && response?.success && (response as { exists?: boolean })?.exists === true) {
          setHasEnded(true);
        }
      } catch (checkError) {
        console.warn('useTrainingSessionEnded: session check failed, allowing the session', checkError);
      }
    };

    void checkSession();
    return () => {
      isCancelled = true;
    };
  }, [sessionId, isEnabled]);

  return hasEnded;
}
